import React, { useState, useEffect } from 'react';
import supabase from '../../lib/supabaseClient';
import toast from 'react-hot-toast';

const MoverReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyDateTime, setReplyDateTime] = useState('');
  const [editingReview, setEditingReview] = useState(null);
  const [editReviewText, setEditReviewText] = useState('');
  const [editRating, setEditRating] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [movers, setMovers] = useState([]);
  const [selectedMover, setSelectedMover] = useState(null);
  const [newReview, setNewReview] = useState({
    reviewer_name: '',
    rating: 5,
    review_text: '',
    user_phone: '',
    review_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchMovers();
  }, []);

  const fetchMovers = async () => {
    try {
      const { data, error } = await supabase
        .from('movers_data')
        .select('id, company_name, email, city, state')
        .order('company_name');

      if (error) throw error;
      setMovers(data || []);
    } catch (error) {
    }
  };

  const fetchReviews = async (email = null) => {
    try {
      setLoading(true);
      
      // If email is provided, find the mover first from movers_data table
      if (email && email.trim()) {
        
        // Step 1: Get mover ID from movers_data table by email
        const { data: moverData, error: moverError } = await supabase
          .from('movers_data')
          .select('id, company_name, email')
          .eq('email', email.trim().toLowerCase())
          .single();
        
        
        if (moverError || !moverData) {
          setReviews([]);
          setLoading(false);
          return;
        }
        
        const moverId = moverData.id;
        
        // Step 2: Get all reviews from mover_reviews table with this mover_id (NO FILTER APPLIED)
        const { data: reviewsData, error: reviewsError } = await supabase
          .from('mover_reviews')
          .select(`
            *,
            movers_data:mover_id (
              company_name,
              email,
              city,
              state
            )
          `)
          .eq('mover_id', moverId)
          .order('created_at', { ascending: false });


        if (reviewsError) throw reviewsError;
        setReviews(reviewsData || []);
        
      } else {
        // No email provided - show empty
        setReviews([]);
      }
    } catch (error) {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteReply = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this mover reply?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('mover_reviews')
        .update({ mover_reply: null, mover_reply_date: null })
        .eq('id', reviewId);

      if (error) throw error;

      fetchReviews(searchQuery.trim());
      toast.success('Reply deleted successfully');
    } catch (error) {
      toast.error('Failed to delete reply');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this customer review?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('mover_reviews')
        .delete()
        .eq('id', reviewId);

      if (error) throw error;

      // Refresh the list
      fetchReviews();
      
      toast.success('Review deleted successfully');
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const handleMoverReply = async (reviewId) => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    try {
      const replyDate = replyDateTime
        ? new Date(replyDateTime).toISOString()
        : new Date().toISOString();

      const { error } = await supabase
        .from('mover_reviews')
        .update({ 
          mover_reply: replyText,
          mover_reply_date: replyDate
        })
        .eq('id', reviewId);

      if (error) throw error;

      // Refresh the list
      fetchReviews(searchQuery.trim());
      
      toast.success('Reply updated successfully');
      setReplyingTo(null);
      setReplyText('');
      setReplyDateTime('');
    } catch (error) {
      toast.error('Failed to update reply');
    }
  };

  const handleEditReview = async (reviewId) => {
    if (!editReviewText.trim()) {
      toast.error('Please enter review text');
      return;
    }

    try {
      const { error } = await supabase
        .from('mover_reviews')
        .update({
          review_text: editReviewText,
          rating: editRating
        })
        .eq('id', reviewId);

      if (error) throw error;

      // Refresh the list
      fetchReviews(searchQuery.trim());
      setEditingReview(null);
      setEditReviewText('');
      setEditRating(5);
      
      toast.success('Review updated successfully');
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getRatingStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const handleAddReview = async () => {
    if (!selectedMover || !newReview.reviewer_name.trim() || !newReview.review_text.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Combine selected date with current time
      const reviewDateTime = new Date(newReview.review_date + 'T' + new Date().toTimeString().split(' ')[0]);
      
      const { error } = await supabase
        .from('mover_reviews')
        .insert({
          mover_id: selectedMover.id,
          lead_id: null, // Admin-created review without lead
          reviewer_name: newReview.reviewer_name,
          rating: newReview.rating,
          review_text: newReview.review_text,
          user_phone: newReview.user_phone || 'N/A',
          status: 'approved',
          is_verified: true,
          is_approved: true,
          created_at: reviewDateTime.toISOString()
        });

      if (error) throw error;

      toast.success('Review added successfully!');
      setShowAddModal(false);
      setSelectedMover(null);
      setNewReview({
        reviewer_name: '',
        rating: 5,
        review_text: '',
        user_phone: '',
        review_date: new Date().toISOString().split('T')[0]
      });
      fetchReviews();
    } catch (error) {
      toast.error('Failed to add review');
    }
  };

  // Use all reviews (filtering is done in fetchReviews based on email)
  const filteredReviews = reviews;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Mover Reviews</h2>
          <p className="text-gray-600 mt-1">Manage customer reviews and ratings</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Review
          </button>
          <button
            onClick={fetchReviews}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  fetchReviews(searchQuery.trim());
                }
              }}
              placeholder="Enter mover's email to view their reviews..."
              className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <svg 
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <button
            onClick={() => fetchReviews(searchQuery.trim())}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Search
          </button>
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
                fetchReviews();
              }}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Clear
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">Enter the mover's email address and press Enter or click Search to view all their reviews</p>
      </div>

      {/* Reviews List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading reviews...</p>
          </div>
        ) : filteredReviews.length === 0 ? null : (
          <div className="divide-y divide-gray-200">
            {filteredReviews.map((review) => (
              <div key={review.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900">
                        {review.movers_data?.company_name || 'Unknown Mover'}
                      </h3>
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusColor(review.status)}`}>
                        {review.status.charAt(0).toUpperCase() + review.status.slice(1)}
                      </span>
                      {review.is_verified && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full border border-blue-200">
                          ✓ Verified
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                      {review.movers_data?.city}, {review.movers_data?.state}
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-yellow-500 text-lg">{getRatingStars(review.rating)}</span>
                      <span className="text-sm text-gray-600">({review.rating}/5)</span>
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    {formatDate(review.created_at)}
                  </div>
                </div>

                {/* Review Text Section */}
                {editingReview === review.id ? (
                  <div className="mb-4 p-4 bg-gray-50 border-2 border-orange-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Edit Review:
                    </label>
                    <div className="mb-3">
                      <label className="block text-xs text-gray-600 mb-1">Rating:</label>
                      <select
                        value={editRating}
                        onChange={(e) => setEditRating(Number(e.target.value))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none"
                      >
                        <option value={1}>1 Star</option>
                        <option value={2}>2 Stars</option>
                        <option value={3}>3 Stars</option>
                        <option value={4}>4 Stars</option>
                        <option value={5}>5 Stars</option>
                      </select>
                    </div>
                    <textarea
                      value={editReviewText}
                      onChange={(e) => setEditReviewText(e.target.value)}
                      placeholder="Enter review text..."
                      className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none resize-none"
                      maxLength={1000}
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleEditReview(review.id)}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={() => {
                          setEditingReview(null);
                          setEditReviewText('');
                          setEditRating(5);
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-gray-700">{review.review_text}</p>
                  </div>
                )}

                {/* Mover Reply Section */}
                {review.mover_reply && (
                  <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-semibold text-blue-900">Mover Reply:</span>
                      <span className="text-xs text-blue-600">
                        {formatDate(review.mover_reply_date)}
                      </span>
                    </div>
                    <p className="text-gray-700 text-sm">{review.mover_reply}</p>
                  </div>
                )}

                {/* Reply Form */}
                {replyingTo === review.id && (
                  <div className="mb-4 p-4 bg-white border-2 border-blue-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {review.mover_reply ? 'Edit Mover Reply:' : 'Add Mover Reply:'}
                    </label>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Enter mover's response to this review..."
                      className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none resize-none"
                      maxLength={500}
                    />
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Reply Date &amp; Time:</label>
                      <input
                        type="datetime-local"
                        value={replyDateTime}
                        onChange={(e) => setReplyDateTime(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm"
                      />
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleMoverReply(review.id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        {review.mover_reply ? 'Save Reply' : 'Submit Reply'}
                      </button>
                      <button
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyText('');
                          setReplyDateTime('');
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    {review.user_phone && review.user_phone !== 'N/A' && (
                      <span className="mr-3">📞 {review.user_phone}</span>
                    )}
                    {review.helpful_count > 0 && (
                      <span>{review.helpful_count} people found this helpful</span>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => {
                        setEditingReview(review.id);
                        setEditReviewText(review.review_text);
                        setEditRating(review.rating);
                      }}
                      className="px-4 py-2 bg-orange-100 text-orange-700 border border-orange-300 rounded-lg hover:bg-orange-200 transition-colors text-sm font-medium"
                    >
                      Edit Review
                    </button>
                    {!review.mover_reply && (
                      <button
                        onClick={() => {
                          setReplyingTo(review.id);
                          setReplyDateTime(new Date().toISOString().slice(0, 16));
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        Add Mover Reply
                      </button>
                    )}
                    {review.mover_reply && (
                      <button
                        onClick={() => {
                          setReplyingTo(review.id);
                          setReplyText(review.mover_reply);
                          setReplyDateTime(
                            review.mover_reply_date
                              ? new Date(review.mover_reply_date).toISOString().slice(0, 16)
                              : new Date().toISOString().slice(0, 16)
                          );
                        }}
                        className="px-4 py-2 bg-blue-100 text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
                      >
                        Edit Reply
                      </button>
                    )}
                    {review.mover_reply && (
                      <button
                        onClick={() => deleteReply(review.id)}
                        className="px-4 py-2 bg-red-100 text-red-700 border border-red-300 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
                      >
                        Delete Reply
                      </button>
                    )}
                    <button
                      onClick={() => deleteReview(review.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                    >
                      Delete Review
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Review Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Add Review as Customer</h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedMover(null);
                    setNewReview({
                      reviewer_name: '',
                      rating: 5,
                      review_text: '',
                      user_phone: ''
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Select Mover */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Mover <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedMover?.id || ''}
                  onChange={(e) => {
                    const mover = movers.find(m => m.id === e.target.value);
                    setSelectedMover(mover);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="">-- Select a mover --</option>
                  {movers.map((mover) => (
                    <option key={mover.id} value={mover.id}>
                      {mover.company_name} ({mover.email}) - {mover.city}, {mover.state}
                    </option>
                  ))}
                </select>
              </div>

              {/* Customer Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Customer Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newReview.reviewer_name}
                  onChange={(e) => setNewReview({ ...newReview, reviewer_name: e.target.value })}
                  placeholder="Enter customer full name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  maxLength={100}
                />
              </div>

              {/* Review Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Review Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={newReview.review_date}
                  onChange={(e) => setNewReview({ ...newReview, review_date: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Rating <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setNewReview({ ...newReview, rating: star })}
                      className="focus:outline-none"
                    >
                      <svg
                        className={`w-10 h-10 ${
                          star <= newReview.rating ? 'text-yellow-400' : 'text-gray-300'
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </button>
                  ))}
                  <span className="ml-3 text-lg font-semibold text-gray-700">
                    {newReview.rating} {newReview.rating === 1 ? 'Star' : 'Stars'}
                  </span>
                </div>
              </div>

              {/* Review Text */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Review Text <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newReview.review_text}
                  onChange={(e) => setNewReview({ ...newReview, review_text: e.target.value })}
                  placeholder="Write the customer's review..."
                  className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                  maxLength={500}
                />
                <div className="text-sm text-gray-500 text-right mt-1">
                  {newReview.review_text.length}/500 characters
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-2xl flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedMover(null);
                  setNewReview({
                    reviewer_name: '',
                    rating: 5,
                    review_text: '',
                    user_phone: '',
                    review_date: new Date().toISOString().split('T')[0]
                  });
                }}
                className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleAddReview}
                disabled={!selectedMover || !newReview.reviewer_name.trim() || !newReview.review_text.trim()}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-semibold hover:from-green-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoverReviews;
